import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataTableFilterMeta } from 'primereact/datatable';
import { TablePageState } from '../../types/table';
import { PositiveItem } from '../../types/positiveItem';
import { defaultPositiveItemFilters } from '../../utils/positiveItemTableHelper';

export interface PositiveItemState {
    data: PositiveItem[];
    loading: boolean;
    error: boolean;
    globalFilterValue: string;
    filters: DataTableFilterMeta;
    page: TablePageState;
    toEdit: PositiveItem | undefined;
    toEditImages: PositiveItem | undefined;
    toDelete: PositiveItem[];
    showDeleteSelected: boolean;
    failed: number[];
}

export const initialState: PositiveItemState = {
    data: [],
    loading: true,
    error: false,
    globalFilterValue: "",
    filters: defaultPositiveItemFilters,
    page: { row: 50, first: 0 },
    toEdit: undefined,
    toEditImages: undefined,
    toDelete: [],
    showDeleteSelected: false,
    failed: [],
};

export const positiveItemSlice = createSlice({
    name: 'positiveItem',
    initialState,
    reducers: {
        setData: (state, action: PayloadAction<PositiveItem[]>) => {
            state.data = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setGlobalFilterValue: (state, action: PayloadAction<string>) => {
            state.globalFilterValue = action.payload;
        },
        setFilters: (state, action: PayloadAction<DataTableFilterMeta>) => {
            state.filters = action.payload;
        },
        setPage: (state, action: PayloadAction<TablePageState>) => {
            state.page = action.payload;
        },
        setToEdit: (state, action: PayloadAction<PositiveItem | undefined>) => {
            state.toEdit = action.payload;
        },
        setToEditImages: (state, action: PayloadAction<PositiveItem | undefined>) => {
            state.toEditImages = action.payload;
        },
        setToDelete: (state, action: PayloadAction<PositiveItem[]>) => {
            state.toDelete = action.payload;
        },
        setShowDeleteSelected: (state, action: PayloadAction<boolean>) => {
            state.showDeleteSelected = action.payload;
        },
        addItem: (state, action: PayloadAction<PositiveItem>) => {
            state.data = state.data.concat([action.payload]);
            state.toEdit = undefined;
        },
        updateItem: (state, action: PayloadAction<PositiveItem>) => {
            state.data = state.data.map((positiveItem) => {
                if (positiveItem.id === action.payload.id) {
                    return action.payload;
                }

                return positiveItem;
            });
            state.toEdit = undefined;
        },
        deleteItem: (state, action: PayloadAction<PositiveItem[]>) => {
            const positiveItemIdsToDelete = action.payload.map(positiveItem => positiveItem.id);
            state.data = state.data.filter((positiveItem) => !positiveItemIdsToDelete.includes(positiveItem.id));
            state.toDelete = [];
        },
        setFailed: (state, action: PayloadAction<number[]>) => {
            state.failed = state.failed.concat(action.payload);
        },
    },
});

export const { 
    setData,
    setLoading,
    setError,
    setGlobalFilterValue,
    setFilters,
    setPage,
    setToEdit,
    setToEditImages,
    setToDelete,
    setShowDeleteSelected,
    addItem,
    updateItem,
    deleteItem,
    setFailed,
 } = positiveItemSlice.actions;

export default positiveItemSlice.reducer;
